import React from 'react';
import {
	ApLoaderDefault,
	ApLoaderText,
	ApButtonSecondary,
	colors,
} from '@alixpartners/ui-components';
import { RegionalMap } from './RegionalMap';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { Wrapper, LoadingOverlay, ButtonWrapper } from './RegionalMap.styles';
import { convertToArray } from 'Details/utils';
import { MAP_COLORS, PIE_INACTIVE_COLORS } from './utils';
import { STATES_MAP_CODES } from '../utils';
import { CHART_COLORS } from 'styles/colors';
import { getRegionalMapData } from 'utils/apiRequests';
import isEqual from 'lodash/isEqual';

export class RegionalMapContainer extends React.Component {
	state = {
		data: [],
		isLoading: false,
	};

	componentDidMount() {
		this.fetchData();
	}

	componentWillUnmount() {
		this.unmounted = true;
	}

	componentDidUpdate(prevProps) {
		const { dimension, queryParams } = this.props;

		if (
			dimension.value !== prevProps.dimension.value ||
			!isEqual(queryParams, prevProps.queryParams)
		) {
			this.fetchData();
		}
	}

	getApiParams = () => {
		const { dimension, queryParams } = this.props;
		const isPurchasing = dimension.value === 'purchasing';

		return {
			...queryParams,
			isPurchasing,
			includeState: true,
		};
	};

	fetchData = () => {
		this.setState({ isLoading: true });

		return getRegionalMapData(this.getApiParams())
			.then(data => {
				if (this.unmounted) return;

				this.setState({
					data,
					isLoading: false,
				});
			})
			.catch(() => {
				if (this.unmounted) return;

				this.setState({ isLoading: false });
			});
	};

	getMapSeriesData = () => {
		const { selectedCountry, selectedState, companies } = this.props;
		const { data } = this.state;
		const countryStatesCode = this.getCountryStatesCode();
		let currentData;

		if (countryStatesCode) {
			const countryWithStates = data.find(
				item => item.code === countryStatesCode
			);
			currentData = countryWithStates && countryWithStates.states;
		} else {
			currentData = data;
		}

		const currentSelection = countryStatesCode
			? selectedState
			: selectedCountry;

		if (!currentData) {
			return [];
		}

		const config = {
			borderWidth: 0.5,
			borderColor: colors.grey8,
		};

		const mapData = currentData.map(item => ({
			...item,
			...config,
			id: item.name,
			...(currentSelection
				? {
						color:
							item.name === currentSelection ? MAP_COLORS[0] : MAP_COLORS[1],
				  }
				: {}),
		}));
		const companyData = mapData.map(item => ({
			...item,
			data: companies.map((companyName, index) => {
				const isInactive = currentSelection && item.name !== currentSelection;
				const currentCompany = item.companies.find(
					company => company.name === companyName
				);

				return {
					name: companyName,
					y: currentCompany ? currentCompany.spend : null,
					color: isInactive ? PIE_INACTIVE_COLORS[index] : CHART_COLORS[index],
				};
			}),
		}));

		return companyData;
	};

	getCountryStatesCode = () => {
		const { selectedCountry } = this.props;

		return STATES_MAP_CODES[selectedCountry] || null;
	};

	render() {
		const { isLoading } = this.state;
		const {
			onCountrySelect,
			onStateSelect,
			chartRef,
			currencySymbol,
			selectedCountry,
		} = this.props;
		const countryWithStatesCode = this.getCountryStatesCode();

		return (
			<Wrapper>
				{isLoading && (
					<LoadingOverlay>
						<ApLoaderDefault loaderText={() => <ApLoaderText />} />
					</LoadingOverlay>
				)}
				{countryWithStatesCode && (
					<ButtonWrapper>
						<ApButtonSecondary
							onClick={() => {
								onCountrySelect(null);
								onStateSelect(null);
							}}
							iconName="baseline_arrow_back"
						>
							Back
						</ApButtonSecondary>
					</ButtonWrapper>
				)}
				<RegionalMap
					key={countryWithStatesCode ? 'states-map' : 'world-map'}
					data={this.getMapSeriesData()}
					onSelect={countryWithStatesCode ? onStateSelect : onCountrySelect}
					chartRef={chartRef}
					selectedCountry={selectedCountry}
					currencySymbol={currencySymbol}
				/>
			</Wrapper>
		);
	}
}

export default props => (
	<QueryStringContext.Consumer>
		{({ companies }) => (
			<RegionalMapContainer companies={convertToArray(companies)} {...props} />
		)}
	</QueryStringContext.Consumer>
);
